import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import arrowRight from "../../assets/images/arrow-right-lg.png";
import LoaderComponent from "../../components/Spinner/LoaderComponent";
import moment from "moment";
import ButtonBasic from "../../components/Buttons/ButtonBasic";
import axiosInstance from "../../utils/axiosInstance";
import { transactionFlowNames as flowNames } from "../../constants/Constants";
import { TransactionLogStyle } from "./TransactionsStyle";
import { useDateTime } from "../../components/Helper/DateTime";
import Toast from "../../components/Toast/Toast";
import { nullChecker } from "../../components/Helper/NullChecker";
import { DateFormat } from "../../utils/formatDateTime";

const UnsettleTransactionModal = ({ show, onHide, transactionId }) => {
  const userData = JSON.parse(localStorage.getItem("userData"));
  const { date } = useDateTime();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transactionData, setTransactionData] = useState({});
  const [showNotes, setShowNotes] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [noteComment, setNoteComment] = useState("");

  const getTransactions = useCallback(async () => {
    setIsLoading(true);
    let _transactionData;
    try {
      const apiResponse = await axiosInstance.get(
        `/transactions/${transactionId}`
      );
      if (apiResponse.message.status !== "200") {
        setIsLoading(false);
        Toast(apiResponse.message.description, "error");
      } else {
        setIsLoading(false);
        _transactionData = apiResponse.data;
        setTransactionData(_transactionData);
      }
    } catch (e) {
      setIsLoading(false);
      Toast(e.message, "error");
      setIsLoading(false);
    }
  }, [transactionId]);

  useEffect(() => {
    if (show) {
      getTransactions();
      // Reset states when modal is shown
      setSelectedRow(null);
      setNoteComment("");
    }
  }, [getTransactions, show]);

  const handleAddNotes = async () => {
    if (!noteComment.trim()) {
      return;
    }

    const now = moment();
    const newNote = {
      author: `${userData?.firstName} ${userData?.lastName}`,
      comment: noteComment,
      date: now.format("YYYY-MM-DD"),
      time: now.format("HH:mm"),
    };

    // Update local state immediately for better UX
    setTransactionData((prev) => ({
      ...prev,
      notes: prev.notes ? [...prev.notes, newNote] : [newNote],
    }));

    setNoteComment("");
  };

  const handleRowSelection = (datasource, rowIndex, pairIndex) => {
    const selectedPair = transactionData.matchPair[pairIndex];
    const selectedDatasource =
      datasource === "A"
        ? { id: selectedPair.datasourceAId, name: selectedPair.datasourceA }
        : { id: selectedPair.datasourceBId, name: selectedPair.datasourceB };

    setSelectedRow({
      datasource,
      rowIndex,
      pairIndex,
      datasourceId: selectedDatasource.id,
      datasourceName: selectedDatasource.name,
      pairId: selectedPair.pairId,
    });
  };

  const handleUnsettleTransaction = async () => {
    // Validate selection
    if (!selectedRow) {
      Toast("Please select a transaction", "error");
      return;
    }

    // Validate notes
    if (!transactionData?.notes?.length) {
      Toast("Please add notes", "error");
      return;
    }
    setIsSubmitting(true);

    try {
      const payload = {
        outputId: transactionId,
        reconConfigId: transactionData.reconConfigId,
        datasourceId: selectedRow.datasourceId,
        pairId: selectedRow.pairId,
        payload: {
          transactionData: {
            matchPair: transactionData.matchPair,
            transactionFlow: transactionData.transactionFlow,
          },
          selectedRow: selectedRow,
        },
        notes: transactionData.notes.map((note) => ({
          author: note.author,
          comment: note.comment,
          date: note.date,
          time: note.time,
        })),
      };
      const response = await axiosInstance.post("/recon/unsettle-transaction", {
        data: payload,
      });

      if (response.message.status === "200") {
        Toast(response?.message?.description, "success");
        onHide();
      } else {
        Toast(response?.message?.description, "error");
      }
    } catch (error) {
      Toast(error?.response?.data?.message?.description, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {(isLoading || isSubmitting) && (
        <div
          className="spinner-center"
          style={{ top: "0%", left: "0%", zIndex: 2000 }}
        >
          <LoaderComponent />
        </div>
      )}

      <TransactionLogStyle>
        <Modal
          backdrop={true}
          size="xl"
          show={show}
          onHide={() => {
            onHide();
            setShowNotes(false);
          }}
          aria-labelledby="example-modal-sizes-title-lg"
        >
          <Modal.Header closeButton>
            <div className="color-dark">Unsettle Transaction</div>
          </Modal.Header>
          <Modal.Body scrollable="true">
            <div className="row">
              <div
                className={`${
                  showNotes ? "col-lg-8" : "col-lg-12"
                } col-sm-12 mb-3`}
              >
                <div className="row">
                  <div className="d-flex px-3 align-items-center">
                    <div className="legend-wrapper d-flex align-items-center mb-3">
                      {transactionData?.transactionFlow?.map((flow, i) => {
                        return (
                          <div
                            className="tsg-badge-rounded danger tsg-badge-sm"
                            key={i}
                          >
                            <div className="legend-title color-light">
                              {flowNames.map((obj) =>
                                obj.id === flow ? obj.name : null
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="row">
                      <div
                        className="col-12 overflow-auto"
                        style={{ height: "auto" }}
                      >
                        {transactionData?.matchPair?.map(
                          (
                            { datasourceA, datasourceB, matchingData },
                            pairIndex
                          ) => (
                            <React.Fragment key={pairIndex}>
                              <div className="matching-rule-wrapper my-3">
                                <div className="d-flex justify-content-between mb-3">
                                  <div className="d-flex flex-row align-items-center">
                                    <div className="color-lightBlue flow-badge atm">
                                      {datasourceA}
                                    </div>
                                    <span className="d-inline-flex flex-column mx-2 justify-content-center">
                                      <img
                                        style={{
                                          width: "20px",
                                          filter: "brightness(0.5)",
                                        }}
                                        src={arrowRight}
                                        alt="arrow-right"
                                      />
                                    </span>
                                    <div className="color-success flow-badge switch">
                                      {datasourceB}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <table className="table table-dim-dark table-borderless transaction-flow-modal table-layout-auto fancy-scroll">
                                <thead>
                                  <tr>
                                    <th scope="col"></th>
                                    <th scope="col"></th>
                                    {matchingData.map(
                                      ({ label, matched }, i) => {
                                        return (
                                          <th
                                            scope="col"
                                            key={i}
                                            style={{
                                              color: matched ? "" : "#ef476f",
                                            }}
                                          >
                                            {label}
                                          </th>
                                        );
                                      }
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {matchingData?.[0]?.datasourceA?.map(
                                    (_, rowIndex) => (
                                      <tr
                                        className={
                                          rowIndex ===
                                          matchingData?.[0]?.datasourceA
                                            .length -
                                            1
                                            ? "border-bottom"
                                            : ""
                                        }
                                        key={`A-${rowIndex}`}
                                      >
                                        <td>
                                          <input
                                            type="checkbox"
                                            name="selectedRow"
                                            checked={
                                              selectedRow?.pairIndex ===
                                                pairIndex &&
                                              selectedRow?.datasource === "A" &&
                                              selectedRow?.rowIndex === rowIndex
                                            }
                                            onChange={() =>
                                              handleRowSelection(
                                                "A",
                                                rowIndex,
                                                pairIndex
                                              )
                                            }
                                          />
                                        </td>
                                        <td className="text-bold">
                                          {rowIndex === 0 && datasourceA}
                                        </td>
                                        {matchingData?.map(
                                          (
                                            { datasourceA, matched, label },
                                            i
                                          ) => (
                                            <td
                                              key={i}
                                              style={{
                                                color: matched ? "" : "#ef476f",
                                              }}
                                            >
                                              {label.toLowerCase() ===
                                              "posting date"
                                                ? datasourceA?.[rowIndex]?.value
                                                  ? DateFormat(
                                                      date,
                                                      datasourceA?.[rowIndex]
                                                        ?.value
                                                    )
                                                  : nullChecker(
                                                      datasourceA?.[rowIndex]
                                                        ?.value
                                                    )
                                                : nullChecker(
                                                    datasourceA?.[rowIndex]
                                                      ?.value
                                                  )}
                                            </td>
                                          )
                                        )}
                                      </tr>
                                    )
                                  )}
                                  {matchingData?.[0]?.datasourceB?.map(
                                    (_, rowIndex) => (
                                      <tr key={`B-${rowIndex}`}>
                                        <td>
                                          <input
                                            type="checkbox"
                                            name="selectedRow"
                                            checked={
                                              selectedRow?.pairIndex ===
                                                pairIndex &&
                                              selectedRow?.datasource === "B" &&
                                              selectedRow?.rowIndex === rowIndex
                                            }
                                            onChange={() =>
                                              handleRowSelection(
                                                "B",
                                                rowIndex,
                                                pairIndex
                                              )
                                            }
                                          />
                                        </td>
                                        <td className="text-bold">
                                          {rowIndex === 0 && datasourceB}
                                        </td>
                                        {matchingData?.map(
                                          (
                                            { datasourceB, matched, label },
                                            i
                                          ) => (
                                            <td
                                              key={i}
                                              style={{
                                                color: matched ? "" : "#ef476f",
                                              }}
                                            >
                                              {label?.toLowerCase() ===
                                              "posting date"
                                                ? datasourceB?.[rowIndex]?.value
                                                  ? DateFormat(
                                                      date,
                                                      datasourceB?.[rowIndex]
                                                        ?.value
                                                    )
                                                  : nullChecker(
                                                      datasourceB?.[rowIndex]
                                                        ?.value
                                                    )
                                                : nullChecker(
                                                    datasourceB?.[rowIndex]
                                                      ?.value
                                                  )}
                                            </td>
                                          )
                                        )}
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </React.Fragment>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {showNotes && (
                <div className="col-lg-4 col-sm-12">
                  <div className="notes-wrapper">
                    <h5 className="mb-3 color-gray-dim">Notes</h5>
                    <div className="overflow-auto" style={{ height: "20em" }}>
                      {transactionData?.notes?.map(
                        ({ author, comment, date, time }, i) => (
                          <ul className="rs-notes-container" key={i}>
                            <li className="rs-notes-item">
                              <h6>{author}</h6>
                              <div className="note-comment">{comment}</div>
                              <sub>
                                {date} {time}
                              </sub>
                            </li>
                          </ul>
                        )
                      )}
                    </div>
                    <div className="row mt-3">
                      <div className="col-12 mt-3">
                        <div className="d-flex flex-column">
                          <textarea
                            className="rs-input"
                            placeholder="Write a Note"
                            rows="3"
                            name="comment"
                            value={noteComment}
                            onChange={(e) => setNoteComment(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="w-100 mt-2">
                      <ButtonBasic
                        type="button"
                        classes={"primary px-4 w-100"}
                        title={"Add Note"}
                        onClick={handleAddNotes}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-end w-100 align-items-center">
              <div className="d-flex">
                <ButtonBasic
                  title={showNotes ? "Hide Notes" : "Show Notes"}
                  onClick={() => setShowNotes(!showNotes)}
                  classes="mr-2"
                />
                <ButtonBasic title="Cancel" onClick={onHide} />
                <ButtonBasic
                  title="Done"
                  onClick={handleUnsettleTransaction}
                  classes="primary"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      </TransactionLogStyle>
    </>
  );
};
export default UnsettleTransactionModal;
